import { c as _c } from "react/compiler-runtime";
import { Play } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@osmix/ui/dialog";
import { SortIcon, StarIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

// Медиа-превью отзыва. `kind: "video"` рисует плашку play поверх кадра.
export type ReviewMedia = {
  url: string;
  kind: "photo" | "video";
};
export type ReviewCardData = {
  id: string;
  authorName: string;
  createdAt: string;
  rating: number;
  // Купленный вариант (цвет/размер) одной строкой, если известен.
  variantLabel?: string;
  // Цвет варианта — ключ фильтра-пилюли (если известен).
  variantColor?: string;
  text: string;
  media: ReviewMedia[];
  // Ответ продавца — на бэкенде отзывов пока нет поля ответа (backend gap).
  sellerAnswer?: string;
};
export type ReviewsSummary = {
  average: number;
  count: number;
  // Продано / возвраты — метрик нет в публичном контракте отзывов (backend gap).
  sold?: number;
  returns?: number;
};
type ReviewSort = "newest" | "best" | "worst";
const formatDate = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
    year: "numeric"
  });
};

// Модалка «Отзывы» (Figma reviews). Заголовок = бренд + товар (или магазин),
// сводка, тумблер «только с фото и видео», сортировка и карточки отзывов с
// ответом продавца. Фильтр-пилюли по цветам вариантов строятся из данных.
export function ReviewsModal(t0) {
  const $ = _c(99);
  const {
    open,
    onOpenChange,
    title,
    summary,
    items,
    onLeaveReview
  } = t0;
  const {
    t
  } = useTranslation();
  const [variant, setVariant] = useState(null);
  const [onlyMedia, setOnlyMedia] = useState(false);
  const [sort, setSort] = useState("newest");
  const [sortOpen, setSortOpen] = useState(false);
  let t1;
  if ($[0] !== items) {
    t1 = new Set(items.map(_temp).filter(_temp2));
    $[0] = items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = [...t1];
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const variantColors = t2;
  let list;
  if ($[4] !== items || $[5] !== onlyMedia || $[6] !== sort || $[7] !== variant) {
    list = [...items];
    if (variant) {
      list = list.filter(r_0 => r_0.variantColor === variant);
    }
    if (onlyMedia) {
      list = list.filter(_temp3);
    }
    let t3;
    if ($[9] !== sort) {
      t3 = (a, b) => sort === "best" ? b.rating - a.rating || b.createdAt.localeCompare(a.createdAt) : sort === "worst" ? a.rating - b.rating || b.createdAt.localeCompare(a.createdAt) : b.createdAt.localeCompare(a.createdAt);
      $[9] = sort;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    list.sort(t3);
    $[4] = items;
    $[5] = onlyMedia;
    $[6] = sort;
    $[7] = variant;
    $[8] = list;
  } else {
    list = $[8];
  }
  const visible = list;
  let t3;
  if ($[11] !== t) {
    t3 = s => s === "best" ? t("shop.reviews.sortBest", {
      defaultValue: "\u0421\u043D\u0430\u0447\u0430\u043B\u0430 \u0445\u043E\u0440\u043E\u0448\u0438\u0435"
    }) : s === "worst" ? t("shop.reviews.sortWorst", {
      defaultValue: "\u0421\u043D\u0430\u0447\u0430\u043B\u0430 \u043D\u0435\u0433\u0430\u0442\u0438\u0432\u043D\u044B\u0435"
    }) : t("shop.reviews.sortNewest", {
      defaultValue: "\u0421\u043D\u0430\u0447\u0430\u043B\u0430 \u043D\u043E\u0432\u044B\u0435"
    });
    $[11] = t;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const sortLabel = t3;
  let t4;
  if ($[13] !== title) {
    t4 = <DialogHeader className="space-y-0"><DialogTitle className="text-[20px] font-medium leading-[28px] text-osmix-text-1">{title}</DialogTitle></DialogHeader>;
    $[13] = title;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const t5 = !variant;
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => setVariant(null);
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t) {
    t7 = t("shop.reviews.all", {
      defaultValue: "\u0412\u0441\u0435 \u043E\u0442\u0437\u044B\u0432\u044B"
    });
    $[16] = t;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t5 || $[19] !== t7) {
    t8 = <VariantPill active={t5} onClick={t6}>{t7}</VariantPill>;
    $[18] = t5;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== variant || $[22] !== variantColors) {
    t9 = variantColors.map(c => <VariantPill key={c} active={variant === c} onClick={() => setVariant(c)}>{c}</VariantPill>);
    $[21] = variant;
    $[22] = variantColors;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t8 || $[25] !== t9) {
    t10 = <div className="flex flex-wrap items-center gap-2">{t8}{t9}</div>;
    $[24] = t8;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== summary.average) {
    t11 = summary.average.toFixed(1);
    $[27] = summary.average;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== t11) {
    t12 = <span className="type-h1-bold text-osmix-text-1">{t11}</span>;
    $[29] = t11;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== summary.average) {
    t13 = <StaticStars value={summary.average} size={12} accent={true} />;
    $[31] = summary.average;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t12 || $[34] !== t13) {
    t14 = <div className="flex flex-col gap-1">{t12}{t13}</div>;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  const t15 = summary.sold != null ? String(summary.sold) : "\u2014";
  let t16;
  if ($[36] !== t) {
    t16 = t("shop.reviews.sold", {
      defaultValue: "\u043F\u0440\u043E\u0434\u0430\u043D\u043E"
    });
    $[36] = t;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t15 || $[39] !== t16) {
    t17 = <SummaryStat value={t15} label={t16} />;
    $[38] = t15;
    $[39] = t16;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  const t18 = String(summary.count);
  let t19;
  if ($[41] !== t) {
    t19 = t("shop.reviews.reviews", {
      defaultValue: "\u043E\u0442\u0437\u044B\u0432\u043E\u0432"
    });
    $[41] = t;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== t18 || $[44] !== t19) {
    t20 = <SummaryStat value={t18} label={t19} />;
    $[43] = t18;
    $[44] = t19;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  const t21 = summary.returns != null ? String(summary.returns) : "\u2014";
  let t22;
  if ($[46] !== t) {
    t22 = t("shop.reviews.returns", {
      defaultValue: "\u0432\u043E\u0437\u0432\u0440\u0430\u0442\u043E\u0432"
    });
    $[46] = t;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== t21 || $[49] !== t22) {
    t23 = <SummaryStat value={t21} label={t22} />;
    $[48] = t21;
    $[49] = t22;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== t14 || $[52] !== t17 || $[53] !== t20 || $[54] !== t23) {
    t24 = <div className="flex items-end gap-5">{t14}{t17}{t20}{t23}</div>;
    $[51] = t14;
    $[52] = t17;
    $[53] = t20;
    $[54] = t23;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== t10 || $[57] !== t24) {
    t25 = <div className="flex flex-col gap-4 rounded-[16px] bg-white px-3 py-4">{t10}{t24}</div>;
    $[56] = t10;
    $[57] = t24;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  let t26;
  if ($[59] !== onlyMedia) {
    t26 = <Switch checked={onlyMedia} onCheckedChange={setOnlyMedia} />;
    $[59] = onlyMedia;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  let t27;
  if ($[61] !== t) {
    t27 = t("shop.reviews.onlyMedia", {
      defaultValue: "\u043F\u043E\u043A\u0430\u0437\u044B\u0432\u0430\u0442\u044C \u0442\u043E\u043B\u044C\u043A\u043E \u043E\u0442\u0437\u044B\u0432\u044B \u0441 \u0444\u043E\u0442\u043E \u0438 \u0432\u0438\u0434\u0435\u043E"
    });
    $[61] = t;
    $[62] = t27;
  } else {
    t27 = $[62];
  }
  let t28;
  if ($[63] !== t26 || $[64] !== t27) {
    t28 = <label className="flex cursor-pointer items-center gap-2.5 text-left type-body2 text-osmix-text-1">{t26}{t27}</label>;
    $[63] = t26;
    $[64] = t27;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  let t29;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = () => setSortOpen(_temp4);
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] !== t) {
    t30 = t("shop.reviews.sortLabel", {
      defaultValue: "\u0421\u043E\u0440\u0442\u0438\u0440\u043E\u0432\u043A\u0430"
    });
    $[67] = t;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  let t31;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <SortIcon />;
    $[69] = t31;
  } else {
    t31 = $[69];
  }
  let t32;
  if ($[70] !== t30) {
    t32 = <button type="button" onClick={t29} aria-label={t30} className="flex items-center gap-2 type-body2 text-osmix-text-1 outline-none">{t31}</button>;
    $[70] = t30;
    $[71] = t32;
  } else {
    t32 = $[71];
  }
  let t33;
  if ($[72] !== sort || $[73] !== sortLabel || $[74] !== sortOpen) {
    t33 = sortOpen && <div className="absolute right-0 top-8 z-20 flex min-w-[172px] flex-col rounded-[10px] bg-white p-[3px] shadow-[0_10px_20px_rgba(0,0,0,0.1)]">{(["newest", "best", "worst"] as ReviewSort[]).map(s_0 => <button key={s_0} type="button" onClick={() => {
        setSort(s_0);
        setSortOpen(false);
      }} className={cn("flex h-[39px] items-center whitespace-nowrap rounded-[8px] px-3 text-left type-body2 text-osmix-text-1 hover:bg-[#1F1F1F]/[0.04]", sort === s_0 && "bg-[#1F1F1F]/[0.04]")}>{sortLabel(s_0)}</button>)}</div>;
    $[72] = sort;
    $[73] = sortLabel;
    $[74] = sortOpen;
    $[75] = t33;
  } else {
    t33 = $[75];
  }
  let t34;
  if ($[76] !== t32 || $[77] !== t33) {
    t34 = <div className="relative shrink-0">{t32}{t33}</div>;
    $[76] = t32;
    $[77] = t33;
    $[78] = t34;
  } else {
    t34 = $[78];
  }
  let t35;
  if ($[79] !== t28 || $[80] !== t34) {
    t35 = <div className="flex items-center justify-between gap-3">{t28}{t34}</div>;
    $[79] = t28;
    $[80] = t34;
    $[81] = t35;
  } else {
    t35 = $[81];
  }
  let t36;
  if ($[82] !== onLeaveReview || $[83] !== t) {
    t36 = onLeaveReview && <Button variant="gray" size="md" className="w-fit self-start justify-self-start" onClick={onLeaveReview}>{t("shop.reviews.leave", {
        defaultValue: "\u041E\u0441\u0442\u0430\u0432\u0438\u0442\u044C \u043E\u0442\u0437\u044B\u0432"
      })}</Button>;
    $[82] = onLeaveReview;
    $[83] = t;
    $[84] = t36;
  } else {
    t36 = $[84];
  }
  let t37;
  if ($[85] !== items.length || $[86] !== t || $[87] !== visible) {
    t37 = visible.length === 0 ? <p className="py-8 text-center type-body2 text-osmix-icon">{items.length === 0 ? t("shop.reviews.empty", {
        defaultValue: "\u041F\u043E\u043A\u0430 \u043D\u0435\u0442 \u043E\u0442\u0437\u044B\u0432\u043E\u0432"
      }) : t("shop.reviews.emptyFiltered", {
        defaultValue: "\u041D\u0435\u0442 \u043E\u0442\u0437\u044B\u0432\u043E\u0432 \u043F\u043E \u0432\u044B\u0431\u0440\u0430\u043D\u043D\u043E\u043C\u0443 \u0444\u0438\u043B\u044C\u0442\u0440\u0443"
      })}</p> : <ul className="flex flex-col gap-5">{visible.map(_temp5)}</ul>;
    $[85] = items.length;
    $[86] = t;
    $[87] = visible;
    $[88] = t37;
  } else {
    t37 = $[88];
  }
  let t38;
  if ($[89] !== t25 || $[90] !== t35 || $[91] !== t36 || $[92] !== t37 || $[93] !== t4) {
    t38 = <DialogContent overlayClassName="bg-black/20 backdrop-blur-[2px]" className="max-h-[90vh] gap-5 overflow-y-auto rounded-[20px] border-0 bg-[#F8F8F8] p-[30px] shadow-none sm:max-w-[610px] [&>[data-slot=dialog-close]]:right-[20px] [&>[data-slot=dialog-close]]:top-[30px] [&>[data-slot=dialog-close]]:opacity-100 [&>[data-slot=dialog-close]_svg]:size-6">{t4}{t25}{t35}{t36}{t37}</DialogContent>;
    $[89] = t25;
    $[90] = t35;
    $[91] = t36;
    $[92] = t37;
    $[93] = t4;
    $[94] = t38;
  } else {
    t38 = $[94];
  }
  let t39;
  if ($[95] !== onOpenChange || $[96] !== open || $[97] !== t38) {
    t39 = <Dialog open={open} onOpenChange={onOpenChange}>{t38}</Dialog>;
    $[95] = onOpenChange;
    $[96] = open;
    $[97] = t38;
    $[98] = t39;
  } else {
    t39 = $[98];
  }
  return t39;
}
function _temp5(r_2) {
  return <ReviewRow key={r_2.id} review={r_2} />;
}
function _temp4(o) {
  return !o;
}
function _temp3(r_1) {
  return r_1.media.length > 0;
}
function _temp2(v) {
  return Boolean(v);
}
function _temp(r) {
  return r.variantColor;
}
function ReviewRow(t0) {
  const $ = _c(30);
  const {
    review
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== review.authorName) {
    t1 = <span className="type-body2-bold text-osmix-text-1">{review.authorName}</span>;
    $[0] = review.authorName;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== review.createdAt) {
    t2 = formatDate(review.createdAt);
    $[2] = review.createdAt;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <span className="type-small text-osmix-icon">{t2}</span>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t3) {
    t4 = <div className="flex items-center gap-2">{t1}{t3}</div>;
    $[6] = t1;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== review.id || $[10] !== review.media) {
    t5 = review.media.length > 0 && <div className="flex gap-1 overflow-x-auto">{review.media.map((m, i) => <div key={`${review.id}-${i}`} className="relative h-[106px] w-20 shrink-0 overflow-hidden rounded-[4px]"><img src={m.url} alt="" className="size-full object-cover" />{m.kind === "video" && <span className="absolute inset-0 flex items-center justify-center bg-black/25"><Play className="size-5 fill-white text-white" /></span>}</div>)}</div>;
    $[9] = review.id;
    $[10] = review.media;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== review.rating) {
    t6 = <StaticStars value={review.rating} size={14} />;
    $[12] = review.rating;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== review.variantLabel) {
    t7 = review.variantLabel && <span className="type-body2 text-osmix-text-1">{review.variantLabel}</span>;
    $[14] = review.variantLabel;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t6 || $[17] !== t7) {
    t8 = <div className="flex items-center gap-2">{t6}{t7}</div>;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== review.text) {
    t9 = review.text && <p className="whitespace-pre-line type-body2 text-osmix-text-1">{review.text}</p>;
    $[19] = review.text;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== review.sellerAnswer || $[22] !== t) {
    t10 = review.sellerAnswer && <div className="flex flex-col gap-1 pl-4"><span className="type-body2-bold text-osmix-text-2">{t("shop.reviews.answer", {
          defaultValue: "\u041E\u0442\u0432\u0435\u0442:"
        })}</span><p className="type-body2 text-osmix-text-2">{review.sellerAnswer}</p></div>;
    $[21] = review.sellerAnswer;
    $[22] = t;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t4 || $[26] !== t5 || $[27] !== t8 || $[28] !== t9) {
    t11 = <li className="flex flex-col gap-3 rounded-[16px] bg-white px-2.5 py-3">{t4}{t5}{t8}{t9}{t10}</li>;
    $[24] = t10;
    $[25] = t4;
    $[26] = t5;
    $[27] = t8;
    $[28] = t9;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  return t11;
}
function VariantPill(t0) {
  const $ = _c(6);
  const {
    active,
    onClick,
    children
  } = t0;
  const t1 = active ? "border-osmix-tangelo bg-osmix-tangelo/5" : "border-[#1F1F1F]/[0.04] bg-[#1F1F1F]/[0.03] hover:bg-[#1F1F1F]/[0.06]";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("h-8 rounded-full border-[0.5px] px-3 type-body2 text-osmix-text-1 outline-none transition-colors", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== children || $[3] !== onClick || $[4] !== t2) {
    t3 = <button type="button" onClick={onClick} className={t2}>{children}</button>;
    $[2] = children;
    $[3] = onClick;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
function SummaryStat(t0) {
  const $ = _c(7);
  const {
    value,
    label
  } = t0;
  let t1;
  if ($[0] !== value) {
    t1 = <span className="type-body1-bold text-osmix-text-1">{value}</span>;
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <span className="type-small text-osmix-text-2">{label}</span>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex flex-col items-center">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}

// Пятизвёздочный статичный рейтинг (заливка по значению).
// `accent` — оранжевые звёзды сводки (Figma), иначе тёмные звёзды карточки отзыва.
function StaticStars(t0) {
  const $ = _c(8);
  const {
    value,
    size: t1,
    accent
  } = t0;
  const size = t1 === undefined ? 14 : t1;
  const t2 = `${value} / 5`;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [0, 1, 2, 3, 4];
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] !== accent || $[2] !== size || $[3] !== value) {
    t4 = t3.map(i => <StarIcon key={i} size={size} className={cn("shrink-0", i < Math.round(value) ? accent ? "text-osmix-tangelo" : "text-osmix-text-1" : "text-osmix-gray-300")} />);
    $[1] = accent;
    $[2] = size;
    $[3] = value;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t2 || $[6] !== t4) {
    t5 = <span className="inline-flex" aria-label={t2}>{t4}</span>;
    $[5] = t2;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}